import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Trash2, Plus, Printer, Save, Send, Loader2, FileText, Unlock, Lock, BookmarkPlus, Stethoscope, Laugh, Smile, Meh, Annoyed, Frown, Angry, GripVertical, Layout as LayoutIcon, Check, X as XIcon, Mail, History } from "lucide-react";
import { toast } from "sonner";
import Layout from "@/components/Layout";

const SYSTEMIC = ["Diabetes","Hypertension","Cardiac","Asthma","Thyroid","Tuberculosis","Epilepsy"];

// Diagnosis-based template categories for ophthalmology OPD
const OPD_TPL_CATEGORIES = [
  "Cataract", "Glaucoma", "Diabetic Retinopathy", "AMD / Retina",
  "Refractive Error", "Dry Eye", "Conjunctivitis / Infection", "Allergy",
  "Cornea", "Squint / Pediatric", "Oculoplasty", "Neuro-ophthalmology",
  "Post-op Follow-up", "Other",
];

const eyePair = (label, val, onChange, keys = ["od","os"]) => (
  <div>
    <Label className="text-xs">{label}</Label>
    <div className="mt-1 grid grid-cols-2 gap-2">
      {keys.map(k => (
        <Input key={k} placeholder={k.toUpperCase()} value={val[k] || ""} onChange={(e) => onChange({ ...val, [k]: e.target.value })} data-testid={`opd-${label.toLowerCase().replace(/[^a-z]/g,"-")}-${k}`} />
      ))}
    </div>
  </div>
);

const refractionEye = (eye, val, onChange) => (
  <div className="rounded-md border border-border p-3">
    <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-brand">{eye}</div>
    <div className="grid grid-cols-5 gap-2">
      {["sph","cyl","axis","add","va"].map(k => (
        <div key={k}>
          <Label className="text-[10px] uppercase text-ink-subtle">{k}</Label>
          <Input value={val[k] || ""} onChange={(e) => onChange({ ...val, [k]: e.target.value })} className="h-8" />
        </div>
      ))}
    </div>
  </div>
);

const emptyEye = { od: "", os: "" };
const emptyRefraction = { sph: "", cyl: "", axis: "", add: "", va: "" };

export default function OpdConsultation() {
  const { visitId } = useParams();
  const [visit, setVisit] = useState(null);
  const [patient, setPatient] = useState(null);
  const [c, setC] = useState({
    chief_complaints: "", duration: "", history_present_illness: "",
    past_ocular_history: "", past_medical_history: "", surgical_history: "",
    allergy: "", current_medication: "", family_history: "",
    systemic_illness: [],
    va_unaided: { ...emptyEye }, va_bcva: { ...emptyEye }, va_pinhole: { ...emptyEye }, va_near: { ...emptyEye },
    refraction_od: { ...emptyRefraction }, refraction_os: { ...emptyRefraction },
    iop: { ...emptyEye }, slit_lamp: { ...emptyEye }, fundus: { ...emptyEye },
    lacrimal: "", color_vision: "", ocular_motility: "", pupils: "",
    diagnosis: "", icd_code: "", treatment_advice: "",
    prescription: [],
    investigations_advised: [], procedure_advised: [], surgery_advised: [],
    follow_up_date: "", referral: "", remarks: "",
    pain_assessment: { score: 0, site: "", character: "", frequency: "", onset: "", aggravating: "", relieving: "" },
    nutrition_assessment: { weight_kg: "", height_cm: "", bmi: "", weight_loss_pct: "", appetite: "", feeding_route: "", diet: "", food_allergies: "", must_score: "" },
  });
  const [saving, setSaving] = useState(false);
  const [consultationId, setConsultationId] = useState(null);
  const [locked, setLocked] = useState(false);
  const [medicines, setMedicines] = useState([]);
  const [servicesMaster, setServicesMaster] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [tplDialogOpen, setTplDialogOpen] = useState(false);
  const [tplName, setTplName] = useState("");
  const [tplShared, setTplShared] = useState(false);
  const [tplCategory, setTplCategory] = useState("");
  const [tplFilterCategory, setTplFilterCategory] = useState("All");
  const [latestPastConsult, setLatestPastConsult] = useState(null);
  const [pastVisible, setPastVisible] = useState(false);
  const [hasReopened, setHasReopened] = useState(false);  // track UI re-open state for visual cue
  // Email Rx
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailTo, setEmailTo] = useState("");
  const [emailSending, setEmailSending] = useState(false);

  // ---- Layout customization (per-doctor section order, persisted via PATCH /api/auth/me) ----
  const DEFAULT_SECTION_ORDER = ["history", "pain", "nutrition", "visual_acuity", "refraction", "examination", "diagnosis", "prescription"];
  const SECTION_LABEL = {
    history: "History", pain: "Pain assessment", nutrition: "Nutrition assessment",
    visual_acuity: "Visual acuity", refraction: "Refraction", examination: "Examination",
    diagnosis: "Diagnosis & Plan", prescription: "Prescription",
  };
  const [sectionOrder, setSectionOrder] = useState(DEFAULT_SECTION_ORDER);
  const [layoutMode, setLayoutMode] = useState(false);
  const [dragKey, setDragKey] = useState(null);
  const [savingLayout, setSavingLayout] = useState(false);

  useEffect(() => {
    // load visit + patient + existing consultation
    (async () => {
      const queue = await api.get("/patients/visits/queue/today").catch(() => ({ data: { items: [] } }));
      const v = queue.data.items.find(x => x.id === visitId);
      if (v) {
        setVisit(v);
        const { data } = await api.get(`/patients/${v.patient_id}`);
        setPatient(data.patient);
      } else {
        // Not in today's queue — could be a past visit or the queue endpoint
        // hasn't refreshed yet. Fetch the visit directly, then patient.
        try {
          const { data: vd } = await api.get(`/patients/visits/${visitId}`);
          if (vd?.visit) {
            setVisit(vd.visit);
            const { data: pd } = await api.get(`/patients/${vd.visit.patient_id}`);
            setPatient(pd.patient);
          }
        } catch {
          // Final fallback: legacy path via existing consultation
          try {
            const { data } = await api.get(`/opd/consultations/by-visit/${visitId}`);
            if (data && data.patient_id) {
              const pat = await api.get(`/patients/${data.patient_id}`);
              setPatient(pat.data.patient);
              setVisit({ id: visitId, patient_id: data.patient_id, token_no: data.token_no, visit_date: data.visit_date });
            } else {
              toast.error("Visit not found. It may have been deleted.");
            }
          } catch {
            toast.error("Could not load visit — check the URL or reopen from the OPD queue.");
          }
        }
      }
      const { data: existing } = await api.get(`/opd/consultations/by-visit/${visitId}`);
      if (existing && existing.id) {
        setConsultationId(existing.id);
        setLocked(!!existing.locked);
        // Coerce legacy comma-separated strings -> [{id,name}]
        const coerce = (raw) => Array.isArray(raw)
          ? raw.map((x) => (typeof x === "string" ? { id: x, name: x } : x))
          : (typeof raw === "string" && raw)
            ? raw.split(/[,\n]/).map((s) => s.trim()).filter(Boolean).map((s) => ({ id: s, name: s }))
            : [];
        setC((prev) => ({
          ...prev,
          ...existing,
          investigations_advised: coerce(existing.investigations_advised),
          procedure_advised: coerce(existing.procedure_advised),
          surgery_advised: coerce(existing.surgery_advised),
        }));
      }
      // Masters + templates + optometry pre-fill
      const [med, svc, tpl, optom] = await Promise.all([
        api.get("/masters/medicines").catch(() => ({ data: { items: [] } })),
        api.get("/masters/services").catch(() => ({ data: { items: [] } })),
        api.get("/opd/templates").catch(() => ({ data: { items: [] } })),
        api.get(`/opd/optometry/by-visit/${visitId}`).catch(() => ({ data: {} })),
      ]);
      setMedicines(med.data.items || []);
      setServicesMaster(svc.data.items || []);
      setTemplates(tpl.data.items || []);
      // Optometry pre-fill (existing logic)
      const o = optom.data || {};
      if (o.id && !existing?.id) {
        setC((prev) => ({
          ...prev,
          refraction_od: o.subjective_od || o.auto_refraction_od || prev.refraction_od,
          refraction_os: o.subjective_os || o.auto_refraction_os || prev.refraction_os,
          va_unaided: o.va_unaided || prev.va_unaided,
          va_bcva: o.va_bcva || prev.va_bcva,
        }));
        toast.info("Optometry findings auto-loaded — verify before saving.");
      }

      // History auto-fill from latest past consultation (only when this is a fresh consultation)
      if (!existing?.id) {
        // Try to find the patient_id — it's set above
        const pid = v?.patient_id || existing?.patient_id;
        if (pid) {
          try {
            const past = await api.get(`/opd/consultations/latest-for-patient/${pid}?exclude_visit=${visitId}`);
            const pd = past.data || {};
            if (pd.id) {
              setC((prev) => ({
                ...prev,
                // Chronic history fields — only pre-fill where empty so doctor edits aren't overwritten
                systemic_illness: prev.systemic_illness || pd.systemic_illness || "",
                past_ocular_history: prev.past_ocular_history || pd.past_ocular_history || "",
                past_medical_history: prev.past_medical_history || pd.past_medical_history || "",
                surgical_history: prev.surgical_history || pd.surgical_history || "",
                allergy: prev.allergy || pd.allergy || "",
                family_history: prev.family_history || pd.family_history || "",
                current_medication: prev.current_medication || pd.current_medication || "",
              }));
              toast.info(`Past history auto-filled from visit on ${(pd.created_at || "").slice(0, 10)}.`);
              setLatestPastConsult(pd);
            }
          } catch (_) { /* ignore */ }
        }
      } else {
        // Fetch latest past for the "View previous Rx" button regardless of fresh/existing
        if (existing?.patient_id) {
          api.get(`/opd/consultations/latest-for-patient/${existing.patient_id}?exclude_visit=${visitId}`).then((r) => setLatestPastConsult(r.data || null)).catch(() => {});
        }
      }
    })();
  }, [visitId]);

  // Load saved layout order from current user's prefs (once on mount)
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        const saved = data?.user?.ui_preferences?.opd_section_order;
        if (Array.isArray(saved) && saved.length > 0) {
          // Merge: keep saved order for known keys, append any new defaults the doctor hasn't seen yet
          const known = saved.filter((k) => DEFAULT_SECTION_ORDER.includes(k));
          const missing = DEFAULT_SECTION_ORDER.filter((k) => !known.includes(k));
          setSectionOrder([...known, ...missing]);
        }
      } catch (_) { /* ignore */ }
    })();
    // eslint-disable-next-line
  }, []);

  // Drag-drop handlers (only active in layout mode)
  const onSectionDragStart = (key) => (e) => {
    if (!layoutMode) return;
    setDragKey(key);
    e.dataTransfer.effectAllowed = "move";
    try { e.dataTransfer.setData("text/plain", key); } catch (_) { /* ignore */ }
  };
  const onSectionDragOver = (e) => { if (layoutMode) e.preventDefault(); };
  const onSectionDrop = (targetKey) => (e) => {
    if (!layoutMode) return;
    e.preventDefault();
    const src = dragKey || (e.dataTransfer && e.dataTransfer.getData("text/plain"));
    if (!src || src === targetKey) { setDragKey(null); return; }
    setSectionOrder((prev) => {
      const next = prev.filter((k) => k !== src);
      const idx = next.indexOf(targetKey);
      next.splice(idx, 0, src);
      return next;
    });
    setDragKey(null);
  };

  const saveLayout = async () => {
    setSavingLayout(true);
    try {
      await api.patch("/auth/me", { ui_preferences: { opd_section_order: sectionOrder } });
      toast.success("Layout saved — your section order will load next time.");
      setLayoutMode(false);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setSavingLayout(false); }
  };

  const resetLayout = () => { setSectionOrder(DEFAULT_SECTION_ORDER); toast.info("Layout reset to default — click Save layout to persist."); };

  // Map of section_key → CSS flex 'order' index, derived from sectionOrder
  const orderOf = (key) => {
    const i = sectionOrder.indexOf(key);
    return i === -1 ? 999 : i;
  };
  const sectionProps = (key) => ({
    draggable: layoutMode,
    onDragStart: onSectionDragStart(key),
    onDragOver: onSectionDragOver,
    onDrop: onSectionDrop(key),
    style: { order: orderOf(key) },
    "data-section-key": key,
    className: layoutMode ? "transition ring-2 ring-dashed ring-brand/40 hover:ring-brand cursor-move" : "",
  });

  const set = (k, v) => setC(s => ({ ...s, [k]: v }));
  const setEye = (k, v) => setC(s => ({ ...s, [k]: v }));
  const addRx = () => set("prescription", [...c.prescription, { medicine: "", medicine_id: "", generic_name: "", strength: "", dosage: "", route: "Topical", eye: "OU", frequency: "", timing: "", duration: "", instructions: "" }]);
  const updateRx = (i, k, v) => {
    const next = [...c.prescription]; next[i] = { ...next[i], [k]: v };
    // When medicine name typed matches a master entry → snapshot generic + medicine_id automatically
    if (k === "medicine") {
      const found = medicines.find((m) => m.name.toLowerCase() === (v || "").toLowerCase());
      if (found) { next[i].medicine_id = found.id; next[i].generic_name = found.generic || ""; next[i].strength = next[i].strength || ""; }
    }
    set("prescription", next);
  };
  const removeRx = (i) => set("prescription", c.prescription.filter((_, idx) => idx !== i));

  const stockOf = (m) => {
    if (m.medicine_id) {
      const x = medicines.find((y) => y.id === m.medicine_id);
      return x ? x.stock : null;
    }
    const x = medicines.find((y) => y.name.toLowerCase() === (m.medicine || "").toLowerCase());
    return x ? x.stock : null;
  };

  const toggleSystemic = (s) => {
    set("systemic_illness", c.systemic_illness.includes(s) ? c.systemic_illness.filter(x => x !== s) : [...c.systemic_illness, s]);
  };

  const save = async (opts = {}) => {
    const { fromBottom = false } = opts;
    setSaving(true);
    try {
      // Snapshot the auto-calculated MUST risk so the saved record carries it for reports/print.
      const bmi = parseFloat(c.nutrition_assessment?.bmi || "");
      const wlp = parseFloat(c.nutrition_assessment?.weight_loss_pct || "");
      let pts = 0;
      if (!Number.isNaN(bmi)) { if (bmi < 18.5) pts += 2; else if (bmi <= 20) pts += 1; }
      if (!Number.isNaN(wlp)) { if (wlp > 10) pts += 2; else if (wlp >= 5) pts += 1; }
      const must_score = pts >= 2 ? "High" : pts === 1 ? "Medium" : "Low";
      const payload = { ...c, visit_id: visitId, nutrition_assessment: { ...c.nutrition_assessment, must_score } };
      const { data: saved } = await api.post("/opd/consultations", payload);
      if (saved && saved.id) setConsultationId(saved.id);  // capture id for subsequent reopen without refresh
      setLocked(true);
      setHasReopened(false);
      // Decide whether the consultation has enough substance to be worth saving as template.
      const hasSubstance = (c.diagnosis || "").trim().length > 0
        || (c.treatment_advice || "").trim().length > 0
        || (c.prescription || []).some((m) => (m.medicine || "").trim().length > 0)
        || (c.investigations_advised || []).length > 0
        || (c.procedure_advised || []).length > 0;
      // Bottom-save: auto-open Save-as-template dialog directly (per UX request).
      // Header-save: keep the lightweight toast action for backward compatibility.
      if (fromBottom && hasSubstance && !hasReopened) {
        toast.success("Consultation saved & locked", { duration: 3000 });
        setTimeout(() => setTplDialogOpen(true), 350);
      } else {
        toast.success("Consultation saved & locked", {
          description: hasSubstance ? "Click below to save this as a reusable template." : undefined,
          action: hasSubstance ? { label: "Save as template", onClick: () => setTplDialogOpen(true) } : undefined,
          duration: 6000,
        });
      }
      // Auto-open the printable Rx in a new tab after a genuine save (not
      // after a bare-edit reopen). The print page auto-prints itself and
      // now also pulls Optometry data as fallback for VA / Refraction.
      if (hasSubstance && !hasReopened) {
        setTimeout(() => window.open(`/print/consultation/${visitId}`, "_blank"), 400);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const reopen = async () => {
    if (!consultationId) return;
    try {
      await api.post(`/opd/consultations/${consultationId}/reopen`);
      toast.success("Consultation reopened — make your edits and Save again.");
      setLocked(false);
      setHasReopened(true);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  const applyTemplate = (tplId) => {
    if (!tplId) return;
    const tpl = templates.find((t) => t.id === tplId);
    if (!tpl) return;
    // Merge — patient-specific fields (chief_complaints, follow_up_date) preserved if user already typed them.
    const t = tpl.template || {};
    setC((prev) => ({
      ...prev,
      // History
      ...["systemic_illness", "past_ocular_history", "past_medical_history", "surgical_history", "allergy"].reduce((a, k) => { if (t[k]) a[k] = t[k]; return a; }, {}),
      // Plan
      ...["diagnosis", "icd_code", "treatment_advice"].reduce((a, k) => { if (t[k]) a[k] = t[k]; return a; }, {}),
      prescription: t.prescription?.length ? t.prescription : prev.prescription,
      investigations_advised: t.investigations_advised?.length ? t.investigations_advised : prev.investigations_advised,
      procedure_advised: t.procedure_advised?.length ? t.procedure_advised : prev.procedure_advised,
      surgery_advised: t.surgery_advised?.length ? t.surgery_advised : prev.surgery_advised,
    }));
    toast.success(`Template "${tpl.name}" applied`);
  };

  const quickFillFromLast = async () => {
    // Always re-fetch the latest (in case patient saw another doctor since page load)
    try {
      const pid = visit?.patient_id || latestPastConsult?.patient_id;
      if (!pid) { toast.error("Patient not loaded yet."); return; }
      const { data: pd } = await api.get(`/opd/consultations/latest-for-patient/${pid}?exclude_visit=${visitId}`);
      if (!pd?.id) { toast.error("No previous consultation found for this patient."); return; }
      setLatestPastConsult(pd);
      setC((prev) => ({
        ...prev,
        // Carry forward EVERYTHING that's typically tweaked on follow-up:
        // (1) Chronic history — only fill if currently empty (so any new doctor notes aren't blown away)
        systemic_illness: prev.systemic_illness || pd.systemic_illness || "",
        past_ocular_history: prev.past_ocular_history || pd.past_ocular_history || "",
        past_medical_history: prev.past_medical_history || pd.past_medical_history || "",
        surgical_history: prev.surgical_history || pd.surgical_history || "",
        allergy: prev.allergy || pd.allergy || "",
        family_history: prev.family_history || pd.family_history || "",
        current_medication: prev.current_medication || pd.current_medication || "",
        // (2) Clinical plan — overwrite (doctor is doing a follow-up of the same problem)
        diagnosis: pd.diagnosis || prev.diagnosis,
        icd_code: pd.icd_code || prev.icd_code,
        treatment_advice: pd.treatment_advice || prev.treatment_advice,
        // (3) Prescription / investigations / procedures — overwrite if the previous one had them
        prescription: pd.prescription?.length ? pd.prescription : prev.prescription,
        investigations_advised: pd.investigations_advised?.length ? pd.investigations_advised : prev.investigations_advised,
        procedure_advised: pd.procedure_advised?.length ? pd.procedure_advised : prev.procedure_advised,
        surgery_advised: pd.surgery_advised?.length ? pd.surgery_advised : prev.surgery_advised,
      }));
      toast.success(`Pre-filled from visit on ${(pd.created_at || "").slice(0, 10)} — verify before saving.`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to load previous consultation");
    }
  };

  const saveAsTemplate = async () => {
    if (!tplName.trim()) { toast.error("Please name the template."); return; }
    const snapshot = {
      systemic_illness: c.systemic_illness, past_ocular_history: c.past_ocular_history,
      past_medical_history: c.past_medical_history, surgical_history: c.surgical_history, allergy: c.allergy,
      diagnosis: c.diagnosis, icd_code: c.icd_code, treatment_advice: c.treatment_advice,
      prescription: c.prescription, investigations_advised: c.investigations_advised,
      procedure_advised: c.procedure_advised, surgery_advised: c.surgery_advised,
    };
    try {
      const { data } = await api.post("/opd/templates", { name: tplName.trim(), category: tplCategory.trim(), template: snapshot, is_shared: tplShared });
      setTemplates((arr) => [...arr, data]);
      toast.success(`Template "${data.name}" saved`);
      setTplDialogOpen(false); setTplName(""); setTplShared(false); setTplCategory("");
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  const [pushing, setPushing] = useState(false);
  const pushToAbdm = async () => {
    if (!patient?.abha_number && !patient?.abha_address) {
      toast.error("Patient has no ABHA linked. Link an ABHA from the patient profile first.");
      return;
    }
    setPushing(true);
    try {
      const { data } = await api.post("/abdm/visit/push", { visit_id: visitId });
      const gs = data.gateway_status || "skipped";
      if (data.mode === "live" && gs === "success") toast.success(`Pushed ${data.count} care contexts to ABDM`);
      else if (data.mode === "live") toast.warning(`ABDM gateway returned: ${gs}`);
      else toast.info(`Queued ${data.count} care contexts (stub — enable ABDM in Settings to go live)`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally { setPushing(false); }
  };

  if (!patient || !visit) return <div className="text-sm text-ink-muted">Loading consultation…</div>;

  const pageAccent = locked
    ? "border-l-4 border-l-emerald-400"
    : hasReopened
      ? "border-l-4 border-l-amber-400"
      : "border-l-4 border-l-sky-300";

  return (
    <div className={`space-y-6 animate-fade-in pb-24 rounded-r-lg pl-2 ${pageAccent}`} data-testid="opd-consult-page">
      <Card className="p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="section-label">OPD · Eye EMR</div>
            <h1 className="font-heading text-2xl font-semibold">{patient.name} <span className="ml-2 text-sm font-body text-ink-muted">{patient.uhid}</span></h1>
            <div className="mt-1 text-xs text-ink-muted">Token #{visit.token_no || "—"} · NABH: time-stamped, doctor-attributed entry · Audit-logged</div>
          </div>
          <div className="flex flex-wrap gap-2">
            {latestPastConsult && (
              <Button variant="outline" onClick={() => setPastVisible(true)} data-testid="opd-past-rx-btn">
                <FileText size={14} className="mr-2" />Past Rx ({(latestPastConsult.created_at || "").slice(0, 10)})
              </Button>
            )}
            <Link to={`/print/consultation/${visitId}`} target="_blank"><Button variant="outline"><Printer size={14} className="mr-2" />Print Rx</Button></Link>
            <Button
              variant="outline"
              onClick={() => { setEmailTo(patient?.email || ""); setEmailDialogOpen(true); }}
              disabled={!consultationId || !(c.prescription || []).length}
              className="border-brand/40 text-brand hover:bg-brand/10"
              data-testid="opd-email-rx-btn"
              title={!consultationId ? "Save the consultation first" : (c.prescription || []).length === 0 ? "Add at least one medicine" : "Email Rx to patient"}
            >
              <Mail size={14} className="mr-2" />Email Rx
            </Button>
            <Button onClick={pushToAbdm} disabled={pushing} variant="outline" className="border-leaf/40 text-leaf-700 hover:bg-leaf-50" data-testid="opd-push-abdm-btn">{pushing ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Send size={14} className="mr-2" />}{pushing ? "Pushing…" : "Push to ABDM"}</Button>
            {locked
              ? <Button onClick={reopen} variant="outline" className="border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100" data-testid="opd-reopen-btn"><Unlock size={14} className="mr-2" />Reopen</Button>
              : <Button onClick={save} disabled={saving} className={hasReopened ? "bg-amber-600 hover:bg-amber-700 text-white" : "bg-brand hover:bg-brand-700"} data-testid="opd-save-btn"><Save size={14} className="mr-2" />{saving ? "Saving…" : (hasReopened ? "Re-save (after reopen)" : "Save consultation")}</Button>}
          </div>
        </div>
      </Card>

      {/* Prominent full-width state banner (always visible) */}
      {locked && (
        <div className="flex items-center gap-3 rounded-lg border-l-4 border-emerald-500 bg-gradient-to-r from-emerald-50 to-emerald-50/50 px-4 py-3 shadow-sm animate-fade-in" data-testid="opd-state-banner-saved">
          <div className="grid h-9 w-9 place-items-center rounded-full bg-emerald-500 text-white"><Lock size={16} /></div>
          <div className="flex-1">
            <div className="font-heading text-sm font-semibold text-emerald-900">Saved &amp; locked</div>
            <div className="text-[11px] text-emerald-700">This consultation has been signed off and is read-only. Click <b>Reopen</b> above to make further edits.</div>
          </div>
        </div>
      )}
      {hasReopened && !locked && (
        <div className="flex items-center gap-3 rounded-lg border-l-4 border-amber-500 bg-gradient-to-r from-amber-50 to-amber-50/50 px-4 py-3 shadow-sm animate-fade-in" data-testid="opd-state-banner-reopened">
          <div className="grid h-9 w-9 place-items-center rounded-full bg-amber-500 text-white"><Unlock size={16} /></div>
          <div className="flex-1">
            <div className="font-heading text-sm font-semibold text-amber-900">Editing reopened consultation</div>
            <div className="text-[11px] text-amber-700">You're editing a previously-saved consultation. Changes won't be persisted until you click <b>Re-save</b>.</div>
          </div>
        </div>
      )}
      {!locked && !hasReopened && (
        <div className="flex items-center gap-3 rounded-lg border-l-4 border-sky-400 bg-gradient-to-r from-sky-50 to-sky-50/50 px-4 py-3 shadow-sm" data-testid="opd-state-banner-draft">
          <div className="grid h-9 w-9 place-items-center rounded-full bg-sky-500 text-white"><Stethoscope size={16} /></div>
          <div className="flex-1">
            <div className="font-heading text-sm font-semibold text-sky-900">Draft consultation — not yet saved</div>
            <div className="text-[11px] text-sky-700">Fill in the findings &amp; Rx, then click <b>Save consultation</b> at the bottom. Saving will also offer to snapshot this as a reusable template.</div>
          </div>
        </div>
      )}

      <Card className="p-3" data-testid="opd-template-bar">
        <div className="flex flex-wrap items-center gap-3">
          <div className="text-xs font-semibold uppercase tracking-wider text-brand flex items-center gap-1.5"><FileText size={12} />Templates</div>
          <Select onValueChange={applyTemplate} disabled={locked || templates.length === 0}>
            <SelectTrigger className="w-72" data-testid="opd-template-select"><SelectValue placeholder={templates.length === 0 ? "No templates yet" : "Apply a saved template…"} /></SelectTrigger>
            <SelectContent>
              {templates
                .filter((t) => tplFilterCategory === "All" || (t.category || "Other") === tplFilterCategory)
                .map((t) => (
                  <SelectItem key={t.id} value={t.id}>
                    {t.name}{t.category ? ` · ${t.category}` : ""}{t.is_shared ? " · shared" : ""}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
          <Button size="sm" variant="outline" onClick={() => setTplDialogOpen(true)} data-testid="opd-save-template-btn"><BookmarkPlus size={12} className="mr-1" />Save as template</Button>
          <Button
            size="sm"
            variant="outline"
            onClick={quickFillFromLast}
            disabled={locked}
            className="border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
            title={latestPastConsult ? `Last visit: ${(latestPastConsult.created_at || "").slice(0, 10)}` : "Pre-fill diagnosis, Rx & history from this patient's previous visit"}
            data-testid="opd-quickfill-last-btn"
          >
            <History size={12} className="mr-1" />Quick-fill from last visit
          </Button>
          {locked && (
            <div className="ml-auto inline-flex items-center gap-1.5 rounded-full border-2 border-emerald-400 bg-emerald-100 px-3 py-1 text-[11px] font-semibold text-emerald-800" data-testid="opd-locked-banner">
              <Lock size={12} /> Saved & Locked · click <b>Reopen</b> to edit
            </div>
          )}
          {hasReopened && !locked && (
            <div className="ml-auto inline-flex items-center gap-1.5 rounded-full border-2 border-amber-400 bg-amber-100 px-3 py-1 text-[11px] font-semibold text-amber-800" data-testid="opd-reopened-banner">
              <Unlock size={12} /> Reopened · unsaved edits
            </div>
          )}
        </div>
        {templates.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5" data-testid="opd-template-category-filter">
            {["All", ...OPD_TPL_CATEGORIES].map((cat) => {
              const count = cat === "All" ? templates.length : templates.filter((t) => (t.category || "Other") === cat).length;
              if (cat !== "All" && count === 0) return null;
              return (
                <button
                  key={cat}
                  type="button"
                  onClick={() => setTplFilterCategory(cat)}
                  className={`rounded-full border px-2.5 py-0.5 text-[10px] font-medium transition ${tplFilterCategory === cat ? "border-brand bg-brand text-white" : "border-border bg-white text-ink hover:border-brand"}`}
                  data-testid={`opd-tpl-cat-${cat.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
                >
                  {cat} <span className="opacity-70">· {count}</span>
                </button>
              );
            })}
          </div>
        )}
      </Card>

      <Dialog open={tplDialogOpen} onOpenChange={setTplDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-heading text-base flex items-center gap-2"><BookmarkPlus size={16} className="text-brand" />Save as template</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Template name *</Label><Input value={tplName} onChange={(e) => setTplName(e.target.value)} placeholder="e.g. Cataract Pre-op, Diabetic Retinopathy Follow-up…" data-testid="opd-template-name" /></div>
            <div>
              <Label>Diagnosis category</Label>
              <select
                className="mt-1 w-full rounded-md border bg-background px-3 py-2 text-sm"
                value={tplCategory}
                onChange={(e) => setTplCategory(e.target.value)}
                data-testid="opd-template-category"
              >
                <option value="">— Uncategorised —</option>
                {OPD_TPL_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <div className="mt-1 text-[10px] text-ink-muted">Groups templates by diagnosis so you can find them faster.</div>
            </div>
            <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={tplShared} onChange={(e) => setTplShared(e.target.checked)} data-testid="opd-template-shared" /> Share with other doctors in this hospital</label>
            <Button onClick={saveAsTemplate} className="w-full bg-brand hover:bg-brand-700" data-testid="opd-template-save-confirm">Save template</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={pastVisible} onOpenChange={setPastVisible}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle className="font-heading text-base flex items-center gap-2"><FileText size={16} className="text-brand" />Past consultation — {(latestPastConsult?.created_at || "").slice(0, 10)}</DialogTitle></DialogHeader>
          {latestPastConsult ? (
            <div className="max-h-[60vh] overflow-y-auto space-y-3 text-xs" data-testid="opd-past-rx-content">
              {latestPastConsult.diagnosis && <div><b className="text-brand">Diagnosis:</b> {latestPastConsult.diagnosis}</div>}
              {latestPastConsult.treatment_advice && <div><b className="text-brand">Treatment advice:</b> {latestPastConsult.treatment_advice}</div>}
              {latestPastConsult.prescription?.length > 0 && (
                <div>
                  <b className="text-brand">Rx:</b>
                  <table className="mt-1 w-full border border-border text-[11px]">
                    <thead className="bg-secondary"><tr><th className="px-2 py-1 text-left">Medicine</th><th className="px-2 py-1 text-left">Strength</th><th className="px-2 py-1 text-left">Dose</th><th className="px-2 py-1 text-left">Eye</th><th className="px-2 py-1 text-left">Frequency</th><th className="px-2 py-1 text-left">Duration</th></tr></thead>
                    <tbody>
                      {latestPastConsult.prescription.map((m, i) => (
                        <tr key={i} className="border-t border-border"><td className="px-2 py-1">{m.medicine}{m.generic_name ? <div className="text-[10px] italic text-ink-muted">{m.generic_name}</div> : null}</td><td className="px-2 py-1">{m.strength || "—"}</td><td className="px-2 py-1">{m.dosage || "—"}</td><td className="px-2 py-1">{m.eye || "—"}</td><td className="px-2 py-1">{m.frequency || "—"}</td><td className="px-2 py-1">{m.duration || "—"}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <div className="flex gap-2 pt-2">
                {latestPastConsult.visit_id && <Link to={`/print/consultation/${latestPastConsult.visit_id}`} target="_blank"><Button size="sm" variant="outline"><Printer size={12} className="mr-1" />Open full Rx (PDF)</Button></Link>}
              </div>
            </div>
          ) : <div className="text-sm text-ink-muted">No past consultations available for this patient.</div>}
        </DialogContent>
      </Dialog>

      {/* Layout customization toolbar (per-doctor section order) */}
      <Card className="p-3" data-testid="opd-layout-toolbar">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-xs text-ink-muted">
            <LayoutIcon size={14} className="text-brand" />
            {layoutMode
              ? <span><b>Layout mode:</b> drag any section card up or down to reorder, then click <b>Save layout</b>.</span>
              : <span>Section order is set the way you like. Click <b>Customize layout</b> to rearrange.</span>}
          </div>
          <div className="flex gap-2">
            {layoutMode ? (
              <>
                <Button size="sm" variant="ghost" onClick={resetLayout} data-testid="opd-layout-reset"><XIcon size={12} className="mr-1" />Reset to default</Button>
                <Button size="sm" variant="outline" onClick={() => setLayoutMode(false)} data-testid="opd-layout-cancel">Cancel</Button>
                <Button size="sm" onClick={saveLayout} disabled={savingLayout} className="bg-brand hover:bg-brand-700" data-testid="opd-layout-save">
                  <Check size={12} className="mr-1" />{savingLayout ? "Saving…" : "Save layout"}
                </Button>
              </>
            ) : (
              <Button size="sm" variant="outline" onClick={() => setLayoutMode(true)} data-testid="opd-layout-customize">
                <LayoutIcon size={12} className="mr-1" />Customize layout
              </Button>
            )}
          </div>
        </div>
      </Card>

      <div className="flex flex-col gap-6" data-testid="opd-sections">
      <div {...sectionProps("history")}>
      {layoutMode && <div className="mb-1 flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-brand"><GripVertical size={12} />Drag to reorder · {SECTION_LABEL.history}</div>}
      <Card className="p-5">
        <div className="section-label mb-3">History</div>
        <div className="form-grid">
          <div className="md:col-span-2"><Label>Chief complaints</Label><Textarea rows={2} value={c.chief_complaints} onChange={e => set("chief_complaints", e.target.value)} data-testid="opd-cc" /></div>
          <div><Label>Duration</Label><Input value={c.duration} onChange={e => set("duration", e.target.value)} /></div>
          <div className="md:col-span-3"><Label>History of present illness</Label><Textarea rows={2} value={c.history_present_illness} onChange={e => set("history_present_illness", e.target.value)} /></div>
          <div><Label>Past ocular history</Label><Textarea rows={2} value={c.past_ocular_history} onChange={e => set("past_ocular_history", e.target.value)} /></div>
          <div><Label>Past medical history</Label><Textarea rows={2} value={c.past_medical_history} onChange={e => set("past_medical_history", e.target.value)} /></div>
          <div><Label>Surgical history</Label><Textarea rows={2} value={c.surgical_history} onChange={e => set("surgical_history", e.target.value)} /></div>
          <div><Label>Drug allergy</Label><Input value={c.allergy} onChange={e => set("allergy", e.target.value)} /></div>
          <div><Label>Current medication</Label><Input value={c.current_medication} onChange={e => set("current_medication", e.target.value)} /></div>
          <div><Label>Family history</Label><Input value={c.family_history} onChange={e => set("family_history", e.target.value)} /></div>
          <div className="md:col-span-3">
            <Label>Systemic illness</Label>
            <div className="mt-2 flex flex-wrap gap-2">
              {SYSTEMIC.map(s => (
                <button key={s} type="button" onClick={() => toggleSystemic(s)}
                  className={`rounded-full border px-3 py-1 text-xs ${c.systemic_illness.includes(s) ? "border-brand bg-brand text-white" : "border-border bg-white text-ink-muted hover:border-brand hover:text-brand"}`}>
                  {s}
                </button>
              ))}
            </div>
          </div>
        </div>
      </Card>
      </div>

      <div {...sectionProps("pain")}>
      {layoutMode && <div className="mb-1 flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-brand"><GripVertical size={12} />Drag to reorder · {SECTION_LABEL.pain}</div>}
      {/* NABH — Pain Assessment (VAS 0-10) */}
      <Card className="p-5" data-testid="opd-pain-card">
        <div className="mb-3 flex items-center justify-between">
          <div>
            <div className="section-label">Pain assessment</div>
            <div className="text-[11px] text-ink-muted">Visual analog scale (VAS) · NABH-mandated patient assessment</div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-wider text-ink-muted">Score</div>
            <div className={`font-heading text-2xl font-bold ${(c.pain_assessment?.score || 0) >= 7 ? "text-rose-600" : (c.pain_assessment?.score || 0) >= 4 ? "text-amber-600" : "text-emerald-600"}`} data-testid="opd-pain-score">{c.pain_assessment?.score || 0}/10</div>
          </div>
        </div>
        {/* Wong-Baker FACES Pain Scale — 6 faces (0,2,4,6,8,10) */}
        <div className="mb-4">
          <div className="grid grid-cols-6 gap-2">
            {(() => {
              const FACES = [
                { score: 0, Icon: Laugh, label: "No pain", tone: "from-emerald-300 to-emerald-500", text: "text-emerald-50" },
                { score: 2, Icon: Smile, label: "Mild", tone: "from-lime-300 to-lime-500", text: "text-lime-50" },
                { score: 4, Icon: Meh, label: "Moderate", tone: "from-yellow-300 to-amber-500", text: "text-amber-50" },
                { score: 6, Icon: Annoyed, label: "Severe", tone: "from-orange-400 to-orange-600", text: "text-orange-50" },
                { score: 8, Icon: Frown, label: "Very severe", tone: "from-rose-400 to-rose-600", text: "text-rose-50" },
                { score: 10, Icon: Angry, label: "Worst pain", tone: "from-rose-600 to-rose-800", text: "text-white" },
              ];
              const cur = c.pain_assessment?.score || 0;
              return FACES.map(({ score, Icon, label, tone }) => {
                const active = cur === score;
                return (
                  <button
                    key={score}
                    type="button"
                    onClick={() => set("pain_assessment", { ...c.pain_assessment, score })}
                    disabled={locked}
                    className={`group flex flex-col items-center gap-1.5 rounded-xl border-2 p-3 transition ${active ? `border-ink bg-gradient-to-b ${tone} text-white shadow-lg scale-105` : "border-border bg-white text-ink-muted hover:border-brand hover:shadow"}`}
                    data-testid={`opd-pain-vas-${score}`}
                  >
                    <Icon size={36} strokeWidth={active ? 2.2 : 1.6} className={active ? "drop-shadow" : "transition group-hover:scale-110"} />
                    <div className={`font-heading text-xl font-bold ${active ? "text-white" : "text-ink"}`}>{score}</div>
                    <div className={`text-[10px] font-semibold uppercase tracking-wider ${active ? "text-white/90" : "text-ink-muted"}`}>{label}</div>
                  </button>
                );
              });
            })()}
          </div>
          <div className="mt-2 flex justify-between text-[10px] uppercase tracking-wider text-ink-muted">
            <span>0 · No pain</span>
            <span>Wong-Baker FACES Pain Scale</span>
            <span>10 · Worst pain</span>
          </div>
        </div>
        <div className="form-grid">
          <div><Label>Site / location</Label><Input value={c.pain_assessment?.site || ""} onChange={(e) => set("pain_assessment", { ...c.pain_assessment, site: e.target.value })} placeholder="e.g. OD, periorbital" data-testid="opd-pain-site" /></div>
          <div>
            <Label>Character</Label>
            <Select value={c.pain_assessment?.character || ""} onValueChange={(v) => set("pain_assessment", { ...c.pain_assessment, character: v === "_none" ? "" : v })}>
              <SelectTrigger data-testid="opd-pain-character"><SelectValue placeholder="Select…" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_none">—</SelectItem>
                {["Sharp", "Dull", "Burning", "Throbbing", "Aching", "Stabbing", "Gritty / FB sensation"].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Frequency</Label>
            <Select value={c.pain_assessment?.frequency || ""} onValueChange={(v) => set("pain_assessment", { ...c.pain_assessment, frequency: v === "_none" ? "" : v })}>
              <SelectTrigger data-testid="opd-pain-frequency"><SelectValue placeholder="Select…" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_none">—</SelectItem>
                {["Constant", "Intermittent", "On exertion", "On exposure to light", "On blinking", "At night"].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Onset / duration</Label><Input value={c.pain_assessment?.onset || ""} onChange={(e) => set("pain_assessment", { ...c.pain_assessment, onset: e.target.value })} placeholder="e.g. 3 days, gradual" data-testid="opd-pain-onset" /></div>
          <div className="md:col-span-2"><Label>Aggravating factors</Label><Input value={c.pain_assessment?.aggravating || ""} onChange={(e) => set("pain_assessment", { ...c.pain_assessment, aggravating: e.target.value })} placeholder="e.g. bright light, reading" data-testid="opd-pain-aggravating" /></div>
          <div className="md:col-span-2"><Label>Relieving factors</Label><Input value={c.pain_assessment?.relieving || ""} onChange={(e) => set("pain_assessment", { ...c.pain_assessment, relieving: e.target.value })} placeholder="e.g. rest, cold compress" data-testid="opd-pain-relieving" /></div>
        </div>
      </Card>
      </div>

      <div {...sectionProps("nutrition")}>
      {layoutMode && <div className="mb-1 flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-brand"><GripVertical size={12} />Drag to reorder · {SECTION_LABEL.nutrition}</div>}
      {/* NABH — Nutrition Assessment (MUST-style screening) */}
      <Card className="p-5" data-testid="opd-nutrition-card">
        <div className="mb-3 flex items-center justify-between">
          <div>
            <div className="section-label">Nutrition assessment</div>
            <div className="text-[11px] text-ink-muted">MUST-style screening · NABH-mandated patient assessment</div>
          </div>
          {(() => {
            const bmi = parseFloat(c.nutrition_assessment?.bmi || "");
            const wlp = parseFloat(c.nutrition_assessment?.weight_loss_pct || "");
            // Simple MUST: BMI<18.5 = 2pts, 18.5-20 = 1pt; WL>10% = 2, 5-10 = 1; sum -> Low/Med/High
            let pts = 0;
            if (!Number.isNaN(bmi)) { if (bmi < 18.5) pts += 2; else if (bmi <= 20) pts += 1; }
            if (!Number.isNaN(wlp)) { if (wlp > 10) pts += 2; else if (wlp >= 5) pts += 1; }
            const risk = pts >= 2 ? "High" : pts === 1 ? "Medium" : "Low";
            const tone = risk === "High" ? "bg-rose-100 text-rose-700 border-rose-300" : risk === "Medium" ? "bg-amber-100 text-amber-700 border-amber-300" : "bg-emerald-100 text-emerald-700 border-emerald-300";
            return (
              <div className={`rounded-full border-2 px-3 py-1 text-[11px] font-semibold ${tone}`} data-testid="opd-nutrition-risk">MUST risk: {risk}</div>
            );
          })()}
        </div>
        <div className="form-grid">
          <div>
            <Label>Weight (kg)</Label>
            <Input
              type="number"
              inputMode="decimal"
              value={c.nutrition_assessment?.weight_kg || ""}
              onChange={(e) => {
                const w = e.target.value;
                const h = parseFloat(c.nutrition_assessment?.height_cm || "");
                const bmi = (parseFloat(w) > 0 && h > 0) ? (parseFloat(w) / Math.pow(h / 100, 2)).toFixed(1) : "";
                set("nutrition_assessment", { ...c.nutrition_assessment, weight_kg: w, bmi });
              }}
              data-testid="opd-nutrition-weight"
            />
          </div>
          <div>
            <Label>Height (cm)</Label>
            <Input
              type="number"
              inputMode="decimal"
              value={c.nutrition_assessment?.height_cm || ""}
              onChange={(e) => {
                const h = e.target.value;
                const w = parseFloat(c.nutrition_assessment?.weight_kg || "");
                const bmi = (parseFloat(h) > 0 && w > 0) ? (w / Math.pow(parseFloat(h) / 100, 2)).toFixed(1) : "";
                set("nutrition_assessment", { ...c.nutrition_assessment, height_cm: h, bmi });
              }}
              data-testid="opd-nutrition-height"
            />
          </div>
          <div>
            <Label>BMI <span className="text-[10px] text-ink-muted">(auto)</span></Label>
            <Input value={c.nutrition_assessment?.bmi || ""} readOnly className="bg-secondary" data-testid="opd-nutrition-bmi" />
          </div>
          <div>
            <Label>Recent unintentional weight loss (%)</Label>
            <Input
              type="number"
              inputMode="decimal"
              value={c.nutrition_assessment?.weight_loss_pct || ""}
              onChange={(e) => set("nutrition_assessment", { ...c.nutrition_assessment, weight_loss_pct: e.target.value })}
              placeholder="e.g. 5"
              data-testid="opd-nutrition-weight-loss"
            />
          </div>
          <div>
            <Label>Appetite</Label>
            <Select value={c.nutrition_assessment?.appetite || ""} onValueChange={(v) => set("nutrition_assessment", { ...c.nutrition_assessment, appetite: v === "_none" ? "" : v })}>
              <SelectTrigger data-testid="opd-nutrition-appetite"><SelectValue placeholder="Select…" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_none">—</SelectItem>
                {["Good", "Fair", "Poor", "No appetite"].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Feeding route</Label>
            <Select value={c.nutrition_assessment?.feeding_route || ""} onValueChange={(v) => set("nutrition_assessment", { ...c.nutrition_assessment, feeding_route: v === "_none" ? "" : v })}>
              <SelectTrigger data-testid="opd-nutrition-route"><SelectValue placeholder="Select…" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_none">—</SelectItem>
                {["Oral", "NG tube", "PEG", "IV / parenteral", "TPN"].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2"><Label>Diet / restrictions</Label><Input value={c.nutrition_assessment?.diet || ""} onChange={(e) => set("nutrition_assessment", { ...c.nutrition_assessment, diet: e.target.value })} placeholder="e.g. Diabetic, low-salt, soft" data-testid="opd-nutrition-diet" /></div>
          <div className="md:col-span-2"><Label>Food allergies</Label><Input value={c.nutrition_assessment?.food_allergies || ""} onChange={(e) => set("nutrition_assessment", { ...c.nutrition_assessment, food_allergies: e.target.value })} placeholder="e.g. lactose, gluten, peanuts" data-testid="opd-nutrition-allergies" /></div>
        </div>
      </Card>
      </div>

      <div {...sectionProps("visual_acuity")}>
      {layoutMode && <div className="mb-1 flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-brand"><GripVertical size={12} />Drag to reorder · {SECTION_LABEL.visual_acuity}</div>}
      <Card className="p-5">
        <div className="section-label mb-3">Visual acuity</div>
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          {eyePair("Unaided", c.va_unaided, v => setEye("va_unaided", v))}
          {eyePair("BCVA", c.va_bcva, v => setEye("va_bcva", v))}
          {eyePair("Pinhole", c.va_pinhole, v => setEye("va_pinhole", v))}
          {eyePair("Near", c.va_near, v => setEye("va_near", v))}
        </div>
      </Card>
      </div>

      <div {...sectionProps("refraction")}>
      {layoutMode && <div className="mb-1 flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-brand"><GripVertical size={12} />Drag to reorder · {SECTION_LABEL.refraction}</div>}
      <Card className="p-5">
        <div className="section-label mb-3">Refraction</div>
        <div className="grid gap-3 md:grid-cols-2">
          {refractionEye("OD (Right Eye)", c.refraction_od, v => setEye("refraction_od", v))}
          {refractionEye("OS (Left Eye)", c.refraction_os, v => setEye("refraction_os", v))}
        </div>
      </Card>
      </div>

      <div {...sectionProps("examination")}>
      {layoutMode && <div className="mb-1 flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-brand"><GripVertical size={12} />Drag to reorder · {SECTION_LABEL.examination}</div>}
      <Card className="p-5">
        <div className="section-label mb-3">Examination</div>
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
          {eyePair("IOP (mmHg)", c.iop, v => setEye("iop", v))}
          {eyePair("Slit lamp", c.slit_lamp, v => setEye("slit_lamp", v))}
          {eyePair("Fundus", c.fundus, v => setEye("fundus", v))}
          <div><Label>Lacrimal sac</Label><Input value={c.lacrimal} onChange={e => set("lacrimal", e.target.value)} /></div>
          <div><Label>Color vision</Label><Input value={c.color_vision} onChange={e => set("color_vision", e.target.value)} /></div>
          <div><Label>Ocular motility</Label><Input value={c.ocular_motility} onChange={e => set("ocular_motility", e.target.value)} /></div>
          <div><Label>Pupils</Label><Input value={c.pupils} onChange={e => set("pupils", e.target.value)} /></div>
        </div>
      </Card>
      </div>

      <div {...sectionProps("diagnosis")}>
      {layoutMode && <div className="mb-1 flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-brand"><GripVertical size={12} />Drag to reorder · {SECTION_LABEL.diagnosis}</div>}
      <Card className="p-5">
        <div className="section-label mb-3">Diagnosis & Plan</div>
        <div className="form-grid">
          <div className="md:col-span-2"><Label>Diagnosis</Label><Textarea rows={2} value={c.diagnosis} onChange={e => set("diagnosis", e.target.value)} data-testid="opd-diagnosis" /></div>
          <div><Label>ICD-10 code</Label><Input value={c.icd_code} onChange={e => set("icd_code", e.target.value)} /></div>
          <div className="md:col-span-3"><Label>Treatment advice</Label><Textarea rows={2} value={c.treatment_advice} onChange={e => set("treatment_advice", e.target.value)} /></div>
          <div className="md:col-span-3 grid gap-3 md:grid-cols-3">
            <div>
              <Label>Investigations advised</Label>
              <Textarea rows={3}
                value={(c.investigations_advised || []).map((x) => (typeof x === "string" ? x : x.name)).join(", ")}
                onChange={(e) => set("investigations_advised", e.target.value.split(",").map((s) => s.trim()).filter(Boolean).map((s) => ({ id: `custom:${s}`, name: s })))}
                placeholder="Type freely, comma-separated (e.g. OCT macula, HVF 24-2)"
                data-testid="opd-investigations" />
              <div className="mt-1 text-[10px] text-ink-muted">Free text. Masters are maintained separately for pricing.</div>
            </div>
            <div>
              <Label>Procedure advised</Label>
              <Textarea rows={3}
                value={(c.procedure_advised || []).map((x) => (typeof x === "string" ? x : x.name)).join(", ")}
                onChange={(e) => set("procedure_advised", e.target.value.split(",").map((s) => s.trim()).filter(Boolean).map((s) => ({ id: `custom:${s}`, name: s })))}
                placeholder="Type freely, comma-separated"
                data-testid="opd-procedures" />
            </div>
            <div>
              <Label>Surgery advised</Label>
              <Textarea rows={3}
                value={(c.surgery_advised || []).map((x) => (typeof x === "string" ? x : x.name)).join(", ")}
                onChange={(e) => set("surgery_advised", e.target.value.split(",").map((s) => s.trim()).filter(Boolean).map((s) => ({ id: `custom:${s}`, name: s })))}
                placeholder="Type freely, comma-separated"
                data-testid="opd-surgeries" />
            </div>
          </div>
          <div><Label>Referral</Label><Input value={c.referral} onChange={e => set("referral", e.target.value)} /></div>
          <div><Label>Follow-up date</Label><Input type="date" value={c.follow_up_date} onChange={e => set("follow_up_date", e.target.value)} data-testid="opd-followup" /></div>
        </div>
      </Card>
      </div>

      <div {...sectionProps("prescription")}>
      {layoutMode && <div className="mb-1 flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-brand"><GripVertical size={12} />Drag to reorder · {SECTION_LABEL.prescription}</div>}
      <Card className="p-5">
        <div className="mb-3 flex items-center justify-between">
          <div className="section-label">Prescription</div>
          <Button size="sm" variant="outline" onClick={addRx} data-testid="opd-add-rx" disabled={locked}><Plus size={12} className="mr-1" />Add medicine</Button>
        </div>
        {c.prescription.length === 0 && <div className="text-xs text-ink-muted">No medicines yet.</div>}
        <datalist id="opd-medicine-master">
          {medicines.map((m) => <option key={m.id} value={m.name}>{m.generic ? `${m.generic}` : ""}</option>)}
        </datalist>
        <datalist id="opd-frequency-master">
          {["1-0-0", "0-0-1", "1-0-1", "1-1-1", "1-1-1-1", "BD", "TID", "QID", "Q4H", "Q6H", "Q8H", "SOS", "HS", "STAT"].map((x) => <option key={x} value={x} />)}
        </datalist>
        <datalist id="opd-timing-master">
          {["Before food", "After food", "With food", "Empty stomach", "At bedtime", "On waking", "Anytime"].map((x) => <option key={x} value={x} />)}
        </datalist>
        {c.prescription.length > 0 && (
          <div className="overflow-x-auto" data-testid="opd-rx-table">
            <table className="w-full text-xs">
              <thead className="bg-secondary/60 text-left text-[10px] uppercase tracking-wider text-ink-muted">
                <tr>
                  <th className="px-2 py-2 w-8">#</th>
                  <th className="px-2 py-2 min-w-[180px]">Medicine</th>
                  <th className="px-2 py-2">Strength</th>
                  <th className="px-2 py-2">Dose</th>
                  <th className="px-2 py-2">Route</th>
                  <th className="px-2 py-2">Eye</th>
                  <th className="px-2 py-2">Frequency</th>
                  <th className="px-2 py-2">Timing</th>
                  <th className="px-2 py-2">Duration</th>
                  <th className="px-2 py-2">Instructions</th>
                  <th className="px-2 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {c.prescription.map((m, i) => {
                  const stock = stockOf(m);
                  return (
                    <tr key={i}>
                      <td className="px-2 py-1 text-ink-muted">{i + 1}</td>
                      <td className="px-2 py-1">
                        <Input list="opd-medicine-master" placeholder="Medicine" value={m.medicine} onChange={(e) => updateRx(i, "medicine", e.target.value)} className="h-8" data-testid={`rx-medicine-${i}`} />
                        {m.generic_name && <div className="mt-0.5 text-[10px] text-ink-muted" data-testid={`rx-generic-${i}`}>{m.generic_name}</div>}
                        {stock !== null && (
                          <div className={`mt-0.5 text-[10px] font-semibold ${stock > 10 ? "text-emerald-600" : stock > 0 ? "text-amber-600" : "text-rose-600"}`} data-testid={`rx-stock-${i}`}>
                            Stock: {stock}
                          </div>
                        )}
                      </td>
                      <td className="px-2 py-1"><Input placeholder="0.5%" value={m.strength} onChange={(e) => updateRx(i, "strength", e.target.value)} className="h-8 w-20" /></td>
                      <td className="px-2 py-1"><Input placeholder="1 drop" value={m.dosage} onChange={(e) => updateRx(i, "dosage", e.target.value)} className="h-8 w-20" /></td>
                      <td className="px-2 py-1">
                        <Select value={m.route || "Topical"} onValueChange={(v) => updateRx(i, "route", v)}>
                          <SelectTrigger className="h-8 w-24" data-testid={`rx-route-${i}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {["Topical", "Oral", "IV", "IM", "SC", "Subconjunctival", "Intravitreal"].map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-2 py-1">
                        <Select value={m.eye || "OU"} onValueChange={(v) => updateRx(i, "eye", v)}>
                          <SelectTrigger className="h-8 w-16"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {["OD", "OS", "OU", "NA", ""].map((e) => <SelectItem key={e || "none"} value={e || "none"}>{e || "—"}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-2 py-1"><Input list="opd-frequency-master" placeholder="1-0-1" value={m.frequency} onChange={(e) => updateRx(i, "frequency", e.target.value)} className="h-8 w-20" data-testid={`rx-frequency-${i}`} /></td>
                      <td className="px-2 py-1"><Input list="opd-timing-master" placeholder="After meals" value={m.timing} onChange={(e) => updateRx(i, "timing", e.target.value)} className="h-8 w-28" data-testid={`rx-timing-${i}`} /></td>
                      <td className="px-2 py-1"><Input placeholder="5 days" value={m.duration} onChange={(e) => updateRx(i, "duration", e.target.value)} className="h-8 w-20" /></td>
                      <td className="px-2 py-1"><Input placeholder="…" value={m.instructions} onChange={(e) => updateRx(i, "instructions", e.target.value)} className="h-8 min-w-[120px]" /></td>
                      <td className="px-2 py-1"><Button variant="ghost" size="icon" onClick={() => removeRx(i)} disabled={locked}><Trash2 size={14} className="text-rose-500" /></Button></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="mt-2 text-[10px] text-ink-muted">Stock counts are only visible on this screen — they will <b>not</b> appear on the printed Rx. Generic names print under the medicine name.</div>
          </div>
        )}
      </Card>
      </div>
      </div>{/* end flex-col sections wrapper */}

      <div className="sticky bottom-0 -mx-4 flex items-center justify-between border-t border-border bg-white px-4 py-3 shadow-[0_-2px_8px_-2px_rgba(0,0,0,0.05)] md:-mx-6 lg:-mx-8">
        <div className="text-xs text-ink-muted">
          Doctor entries are signed with your account & time-stamped (NABH).
          {locked && <span className="ml-2 font-semibold text-emerald-700" data-testid="opd-state-saved">· Saved & locked</span>}
          {hasReopened && !locked && <span className="ml-2 font-semibold text-amber-700" data-testid="opd-state-reopened">· Reopened — unsaved edits</span>}
        </div>
        {locked
          ? <Button onClick={reopen} variant="outline" className="border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100" data-testid="opd-bottom-reopen-btn"><Unlock size={14} className="mr-2" />Reopen to edit</Button>
          : <div className="flex gap-2">
              <Button variant="outline" onClick={() => setTplDialogOpen(true)} data-testid="opd-bottom-template-btn"><BookmarkPlus size={14} className="mr-2" />Save as template</Button>
              <Button onClick={() => save({ fromBottom: true })} disabled={saving} className={hasReopened ? "bg-amber-600 hover:bg-amber-700 text-white" : "bg-brand hover:bg-brand-700"} data-testid="opd-bottom-save-btn"><Save size={14} className="mr-2" />{saving ? "Saving…" : (hasReopened ? "Re-save (after reopen)" : "Save consultation")}</Button>
            </div>}
      </div>

      {/* Email Rx Dialog */}
      <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">Email prescription to patient</DialogTitle></DialogHeader>
          <p className="text-sm text-ink-muted">A styled HTML prescription will be sent via the hospital's configured Gmail SMTP. The patient receives a printable digital Rx with all medicines, dosages, eye-specific findings, and your signature.</p>
          <div className="mt-3 space-y-2">
            <label className="text-xs font-semibold uppercase tracking-wider text-ink-muted">Patient email</label>
            <input
              type="email" value={emailTo} onChange={(e) => setEmailTo(e.target.value)}
              placeholder={patient?.email ? patient.email : "patient@example.com"}
              className="w-full rounded-md border bg-background px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
              data-testid="opd-email-rx-input"
            />
            {!patient?.email && (
              <p className="text-[11px] text-amber-700">No email on patient's profile. The address entered above will be used for this delivery only — update the patient record separately to persist it.</p>
            )}
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="outline" onClick={() => setEmailDialogOpen(false)} data-testid="opd-email-rx-cancel">Cancel</Button>
            <Button
              onClick={async () => {
                if (!emailTo.trim()) { toast.error("Please enter a recipient email"); return; }
                setEmailSending(true);
                try {
                  const { data } = await api.post(`/opd/consultations/${consultationId}/email-rx`, { email: emailTo.trim() });
                  if (data.ok && !data.stub) toast.success(`Rx emailed to ${data.to_email}`);
                  else if (data.stub) toast.message("Email saved in stub mode — enable Gmail SMTP in Admin → Settings to send for real.");
                  else toast.error(data.message || "Email failed");
                  setEmailDialogOpen(false);
                } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
                finally { setEmailSending(false); }
              }}
              disabled={emailSending}
              className="bg-brand hover:bg-brand-700"
              data-testid="opd-email-rx-send"
            >
              {emailSending ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Mail size={14} className="mr-2" />}
              {emailSending ? "Sending…" : "Send Rx"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
